import { forwardRef, useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import { RotateCcwIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import {
  type PortalConnectionStatusFragment,
  Portals_Types_Enum_Enum,
} from '@graphql/__generated__/graphql';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { CHECK_PORTAL_CONNECTION } from '@modules/portals/portalsQueries';
import { useLocale } from '@shared/hooks/locale';

export const PORTAL_CONNECTION_STATUS_FRAGMENT = gql(/* GraphQL */ `
  fragment PortalConnectionStatus on portals {
    id
    type
  }
`);

type PortalConnectionStatusProps = {
  portal?: PortalConnectionStatusFragment | null;
};

// Portal types whose credentials can be validated by the check-portal-connection
// action (idx via the IDX publisher; idealista/pisos via a live feed probe).
const CONNECTION_TESTABLE_TYPES = new Set<Portals_Types_Enum_Enum>([
  Portals_Types_Enum_Enum.Idx,
  Portals_Types_Enum_Enum.Idealista,
  Portals_Types_Enum_Enum.Pisos,
]);

// idealista/pisos are probed by reading stored feed credentials, which only
// admins can select. For a non-admin the action can only answer "unable to read
// credentials" — a red failure on an otherwise healthy portal — so the check is
// hidden from non-admins for these types. IDX needs no stored credentials and
// stays visible to everyone.
const CREDENTIALED_TYPES = new Set<Portals_Types_Enum_Enum>([
  Portals_Types_Enum_Enum.Idealista,
  Portals_Types_Enum_Enum.Pisos,
]);

export const PortalConnectionStatus = forwardRef<
  HTMLDivElement,
  PortalConnectionStatusProps
>(({ portal }, ref) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const isVisible =
    portal != null &&
    CONNECTION_TESTABLE_TYPES.has(portal.type) &&
    (!CREDENTIALED_TYPES.has(portal.type) || me?.is_admin === true);
  const { data, loading, error, refetch } = useQuery(CHECK_PORTAL_CONNECTION, {
    variables: { id: portal?.id ?? '' },
    skip: !isVisible,
    notifyOnNetworkStatusChange: true,
  });

  const alertSeverity = useMemo(() => {
    if (loading) {
      return 'info';
    }

    if (error) {
      return 'error';
    }

    return data?.check_portal_connection?.success ? 'success' : 'error';
  }, [data, loading, error]);

  const alertTitle = useMemo(() => {
    if (loading) {
      return t('Checking connection...');
    }

    if (error) {
      return t('Error checking connection');
    }

    return data?.check_portal_connection?.success
      ? t('Connection successful')
      : t('Connection failed');
  }, [data, loading, error, t]);

  const alertMessage = useMemo(() => {
    if (loading) {
      return null;
    }

    if (error) {
      return t(
        'An error occured while checking the connection for the portal, details: {{error}}.',
        {
          error: error.message ?? t('No details available'),
        },
      );
    }

    return data?.check_portal_connection?.error
      ? t(
          'The connection failed with the following error: {{error}}. Please check the portal settings and try again.',
          {
            error:
              data.check_portal_connection.error ?? t('No details available'),
          },
        )
      : null;
  }, [data, loading, error, t]);

  if (!isVisible) {
    return null;
  }

  return (
    <Box sx={{ p: 2, pb: 0 }} ref={ref}>
      <Alert
        severity={alertSeverity}
        icon={
          loading ? <CircularProgress size={22} disableShrink /> : undefined
        }
        variant="emphasized"
        action={
          <Button
            startIcon={<RotateCcwIcon />}
            color="inherit"
            size="small"
            disabled={loading}
            onClick={() => refetch()}
          >
            {t('Retry')}
          </Button>
        }
      >
        <AlertTitle sx={alertMessage == null ? { marginBottom: 0 } : undefined}>
          {alertTitle}
        </AlertTitle>
        {alertMessage}
      </Alert>
    </Box>
  );
});
